/* playbackClock.js
   ────────────────
   Client-side playback-time scheduler for the voice agent.

   PROBLEM: realtime providers generate audio faster than it plays. The
   client queues chunks into WebAudio (`nextPlayTime`), so everything keyed
   to frame ARRIVAL (transcript deltas, navigation/tool directives) runs
   seconds ahead of what the speaker is actually saying — the screen
   navigates and the caption finishes before the voice gets there.

   KEY INSIGHT: at the moment any frame arrives,
       lagMs = (nextPlayTime − ctx.currentTime) × 1000
   is exactly how far ahead of the audible playhead that content is.
   Deferring its visible effect by lagMs lands it in sync — no server
   round-trip, self-correcting per frame.

   This module owns two playback-anchored queues plus drain watchers:
     • transcript queue — text fragments revealed karaoke-style
     • directive queue  — navigation/visual tool calls executed when the
       narration they belong to becomes audible
     • watchDrain(cb) — fires when the scheduled audio has fully PLAYED
       OUT (call it on response.done; after that no more chunks arrive
       for the response, so `now + lagMs` is the true playback end)

   The host component injects the lag source + sinks via configure().
*/

const PUMP_INTERVAL_MS = 120;
const MAX_LAG_MS = 20000; // safety clamp — a runaway lag means a stuck ctx

/* Speech-rate pacing ─────────────────────────────────────────────────
   Providers deliver a response's WHOLE transcript within a few seconds
   while its audio takes 15-25s to play. Deferring by lag-at-arrival alone
   undercompensates: when a burst of text arrives, the audio it belongs
   to isn't scheduled yet, so lagMs is small and text still races ahead.
   Fix: additionally pace reveals at the response's measured speech rate
   (audio ms scheduled ÷ chars seen), clamped to sane bounds. */
const MS_PER_CHAR_DEFAULT = 70;  // ≈14 chars/s ≈ 165 wpm TTS
const MS_PER_CHAR_MIN = 40;
const MS_PER_CHAR_MAX = 120;
const RATE_MIN_SAMPLE_CHARS = 40;    // don't trust the estimate below this
const RATE_MIN_SAMPLE_AUDIO_MS = 1500;

let _getLagMs = () => 0;
let _onTranscript = null;      // (text) => void — append revealed text
let _onTranscriptCommit = null;// (fullText) => void — natural end-of-response
let _onDirective = null;       // (name, args, source) => void

const _transcriptQ = []; // { text, revealAt } | { commit: true, fullText, revealAt }
const _directiveQ = [];  // { name, args, source, executeAt }
let _pumpId = null;
let _lastRevealAt = 0;   // monotonic guard — never reveal out of order
let _paceAnchor = 0;     // wall time the NEXT fragment may reveal (speech-rate pacing)
let _rateAudioMs = 0;    // per-response: audio scheduled so far
let _rateChars = 0;      // per-response: transcript chars seen so far
let _lastExecuteAt = 0;
const _drainTimers = new Set();

export function configurePlaybackClock({ getLagMs, onTranscript, onTranscriptCommit, onDirective }) {
  if (getLagMs) _getLagMs = getLagMs;
  if (onTranscript !== undefined) _onTranscript = onTranscript;
  if (onTranscriptCommit !== undefined) _onTranscriptCommit = onTranscriptCommit;
  if (onDirective !== undefined) _onDirective = onDirective;
}

export function playbackLagMs() {
  let lag = 0;
  try { lag = Number(_getLagMs()) || 0; } catch (_) { lag = 0; }
  return Math.min(Math.max(0, lag), MAX_LAG_MS);
}

function _ensurePump() {
  if (_pumpId) return;
  _pumpId = setInterval(_pump, PUMP_INTERVAL_MS);
}

function _maybeStopPump() {
  if (_pumpId && _transcriptQ.length === 0 && _directiveQ.length === 0) {
    clearInterval(_pumpId);
    _pumpId = null;
  }
}

function _pump() {
  const now = performance.now();

  // Transcript reveal — batch every due fragment into one append.
  let revealText = '';
  while (_transcriptQ.length > 0 && _transcriptQ[0].revealAt <= now) {
    const item = _transcriptQ.shift();
    if (item.commit) {
      if (revealText && _onTranscript) { try { _onTranscript(revealText); } catch (_) {} }
      revealText = '';
      if (_onTranscriptCommit) { try { _onTranscriptCommit(item.fullText); } catch (_) {} }
    } else {
      revealText += item.text;
    }
  }
  if (revealText && _onTranscript) { try { _onTranscript(revealText); } catch (_) {} }

  // Directives due for execution.
  while (_directiveQ.length > 0 && _directiveQ[0].executeAt <= now) {
    const d = _directiveQ.shift();
    if (_onDirective) { try { _onDirective(d.name, d.args, d.source); } catch (_) {} }
  }

  _maybeStopPump();
}

/** Report a scheduled audio chunk's duration (called per chunk from the
 *  host's audio path). Feeds the live speech-rate estimate. */
export function noteAudioScheduled(durationMs) {
  if (Number.isFinite(durationMs) && durationMs > 0) _rateAudioMs += durationMs;
}

function _msPerChar() {
  if (_rateChars >= RATE_MIN_SAMPLE_CHARS && _rateAudioMs >= RATE_MIN_SAMPLE_AUDIO_MS) {
    return Math.min(MS_PER_CHAR_MAX, Math.max(MS_PER_CHAR_MIN, _rateAudioMs / _rateChars));
  }
  return MS_PER_CHAR_DEFAULT;
}

/** Queue a transcript fragment to reveal when it becomes audible.
 *  Reveal time = the LATER of (a) arrival + playback lag and (b) the
 *  speech-rate pace anchor — so a burst of early transcript can never
 *  outrun the voice by more than one fragment. */
export function scheduleTranscript(text) {
  if (!text) return;
  const now = performance.now();
  _rateChars += text.length;
  const revealAt = Math.max(now + playbackLagMs(), _paceAnchor, _lastRevealAt);
  _lastRevealAt = revealAt;
  // Next fragment may not reveal until this one has been "spoken".
  _paceAnchor = revealAt + text.length * _msPerChar();
  _transcriptQ.push({ text, revealAt });
  _ensurePump();
}

/** Queue an end-of-response commit AFTER every pending fragment reveals.
 *  Fires onTranscriptCommit(fullText) in-order with the reveals. */
export function scheduleTranscriptCommit(fullText) {
  const revealAt = Math.max(performance.now() + playbackLagMs(), _lastRevealAt);
  _lastRevealAt = revealAt;
  _transcriptQ.push({ commit: true, fullText, revealAt });
  // New response starts fresh: reset the pace anchor + rate sample.
  _paceAnchor = 0;
  _rateAudioMs = 0;
  _rateChars = 0;
  _ensurePump();
}

/** Queue a directive (tool call) to execute when the narration it belongs
 *  to becomes audible. `immediate: true` bypasses the queue (cleanup
 *  directives must not wait). */
export function scheduleDirective(name, args, source, { immediate = false } = {}) {
  if (immediate) {
    if (_onDirective) { try { _onDirective(name, args, source); } catch (_) {} }
    return;
  }
  const executeAt = Math.max(performance.now() + playbackLagMs(), _lastExecuteAt);
  _lastExecuteAt = executeAt;
  _directiveQ.push({ name, args, source, executeAt });
  _ensurePump();
}

/** Fire cb once the scheduled audio has fully played out. Call ONLY after
 *  response.done (no more chunks arrive after that, so now + lagMs is the
 *  exact playback end). Returns a cancel fn. */
export function watchDrain(cb, extraMs = 60) {
  const t = setTimeout(() => {
    _drainTimers.delete(t);
    try { cb(); } catch (_) {}
  }, playbackLagMs() + extraMs);
  _drainTimers.add(t);
  return () => { clearTimeout(t); _drainTimers.delete(t); };
}

/** Drop everything pending (user interrupted / cancelled / voice stopped).
 *  The host commits whatever transcript was already revealed — that is
 *  what the user actually heard. */
export function clearPlaybackQueues() {
  _transcriptQ.length = 0;
  _directiveQ.length = 0;
  _lastRevealAt = 0;
  _lastExecuteAt = 0;
  _paceAnchor = 0;
  _rateAudioMs = 0;
  _rateChars = 0;
  for (const t of _drainTimers) clearTimeout(t);
  _drainTimers.clear();
  _maybeStopPump();
}

/** Full teardown (voice session ended). */
export function stopPlaybackClock() {
  clearPlaybackQueues();
  if (_pumpId) { clearInterval(_pumpId); _pumpId = null; }
}
